import React, { useState, useEffect } from 'react';
import { AppHeader } from '../components/AppHeader';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getUnsyncedTimestamps, markTimestampsSynced, clearSyncedData } from '../services/offlineDb';
import { syncTimestamps } from '../services/api';

export const SyncPage: React.FC = () => {
  const isOnline = useOnlineStatus();
  const [count, setCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(localStorage.getItem('catms_last_synced_at'));

  useEffect(() => {
    getUnsyncedTimestamps().then(ts => setCount(ts.length));
  }, []);

  const handleSync = async () => {
    if (!isOnline) { setResult({ type: 'error', message: 'オフラインのため同期できません。' }); return; }
    setSyncing(true); setResult(null);
    try {
      const unsyncedTs = await getUnsyncedTimestamps();
      if (unsyncedTs.length === 0) { setResult({ type: 'info', message: '同期するデータはありません。' }); setSyncing(false); return; }
      const payload = unsyncedTs.map(t => ({
        local_id: t.localId, timestamp_type: t.timestampType, recorded_at: t.recordedAt,
        latitude: t.latitude, longitude: t.longitude, timestamp_source: t.timestampSource,
        communication_type: t.communicationType, work_session_id: t.workSessionId, vessel_id: t.vesselId,
      }));
      const deviceId = localStorage.getItem('catms_device_id') ?? crypto.randomUUID();
      localStorage.setItem('catms_device_id', deviceId);
      await syncTimestamps(deviceId, payload, 'lte');
      const ids = unsyncedTs.map(t => t.id!).filter(Boolean);
      await markTimestampsSynced(ids);
      const now = new Date().toLocaleString('ja-JP');
      localStorage.setItem('catms_last_synced_at', now);
      setLastSyncedAt(now);
      setCount(0);
      setResult({ type: 'success', message: unsyncedTs.length + '件の同期が完了しました。' });
    } catch (err) {
      setResult({ type: 'error', message: err instanceof Error ? err.message : '同期に失敗しました。' });
    } finally { setSyncing(false); }
  };

  const handleClear = async () => {
    await clearSyncedData();
    setResult({ type: 'success', message: '同期済みデータを削除しました。' });
    setShowClearConfirm(false);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ヘッダー */}
      <AppHeader title="同期管理" />

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* ステータスカード */}
        <div style={{ background: 'white', borderRadius: 16, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>{isOnline ? '🟢' : '🟡'}</span>
              <div>
                <div style={{ fontSize: 13, color: '#94a3b8' }}>接続状態</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: isOnline ? '#16a34a' : '#d97706' }}>
                  {isOnline ? 'オンライン' : 'オフライン'}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>未同期データ</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: count > 0 ? '#ef4444' : '#16a34a' }}>{count}件</div>
            </div>
          </div>
          {lastSyncedAt && (
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🕐</span>
              <span>最終同期: {lastSyncedAt}</span>
            </div>
          )}
        </div>

        {/* 結果メッセージ */}
        {result && (
          <div style={{
            background: result.type === 'success' ? '#f0fdf4' : result.type === 'error' ? '#fef2f2' : '#eff6ff',
            border: '1px solid ' + (result.type === 'success' ? '#bbf7d0' : result.type === 'error' ? '#fecaca' : '#bfdbfe'),
            borderRadius: 12, padding: '12px 16px', fontSize: 13,
            color: result.type === 'success' ? '#16a34a' : result.type === 'error' ? '#dc2626' : '#2563eb',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>{result.type === 'success' ? '✅' : result.type === 'error' ? '❌' : 'ℹ️'}</span>
            <span>{result.message}</span>
          </div>
        )}

        {/* 同期ボタン */}
        <button
          onClick={handleSync}
          disabled={syncing || !isOnline}
          style={{
            width: '100%', padding: '16px', borderRadius: 14, border: 'none',
            background: syncing || !isOnline ? '#e2e8f0' : '#0ea5e9',
            color: syncing || !isOnline ? '#94a3b8' : 'white',
            fontSize: 16, fontWeight: 700, cursor: syncing || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {syncing ? (
            <><span style={{ fontSize: 18 }}>⏳</span> 同期中...</>
          ) : (
            <><span style={{ fontSize: 18 }}>🔄</span> データを今すぐ同期する</>
          )}
        </button>

        {/* 説明 */}
        <div style={{ background: 'white', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>同期について</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { icon: '📶', text: 'オンライン時は打刻データが自動的にサーバーへ送信されます。' },
              { icon: '📵', text: 'オフライン時はデータが端末に保存され、通信復帰後に同期されます。' },
              { icon: '🔄', text: '未同期データがある場合はこの画面から手動で同期できます。' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 同期済みデータ削除 */}
        <button
          onClick={() => setShowClearConfirm(true)}
          style={{ width: '100%', padding: '13px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          🗑️ 同期済みデータを削除する
        </button>
      </div>

      {/* 削除確認モーダル */}
      {showClearConfirm && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', zIndex: 100 }}>
          <div style={{ background: 'white', width: '100%', borderRadius: '20px 20px 0 0', padding: '24px 20px 36px' }}>
            <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 20, textAlign: 'center', marginBottom: 8 }}>🗑️</div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#0f172a', textAlign: 'center', marginBottom: 8 }}>同期済みデータを削除しますか？</div>
            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
              すでにサーバーに送信済みのデータを端末から削除します。この操作は元に戻せません。
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowClearConfirm(false)} style={{ flex: 1, padding: '14px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={handleClear} style={{ flex: 1, padding: '14px', borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>削除する</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
